import {Button as HeadlessUiButton, Transition} from "@headlessui/react";
import type {MouseEvent, ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {Spinner} from "@vesta/aergia/components/spinner";
import {tw} from "@vesta/aergia/utilities/utilities";
import ChevronDownIcon from "@vesta/aergia/svgs/core/chevron-down.svg?react";
import {Tooltip, type TooltipProps} from "@vesta/aergia/components/tooltip";
import {toDataAnalyticsHtmlAttributes} from "@vesta/aergia/utilities/dataAnalytics";

type IconButtonTooltipProps = Omit<TooltipProps, "children">;
// TODO: Add shape
// TODO: Add label
// TODO: The loader appear/exit animations differ from the ones used in ADS
export function IconButton({
    icon: IconComponent,
    disabled,
    isSelected,
    isLoading,
    appearance,
    spacing,
    className,
    testId,
    onClick,
    showChevron,
    tooltip,
    content,
    dataAnalyticsEvent = null,
    dataAnalyticsProps = null,
}: {
    /**
     * className prop needs to be able to control the icon size.
     * Icon color should be picked up through the current text color.
     * Icon should have no background color of its own.
     */
    icon: (props: {className?: string}) => ReactNode;
    appearance?: "default" | "discovery" | "primary" | "subtle";
    disabled?: boolean;
    isSelected?: boolean;
    isLoading?: boolean;
    spacing?: "default" | "compact";
    className?: string;
    testId?: string;
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    showChevron?: boolean;
    tooltip?: IconButtonTooltipProps;
    content?: ReactNode;
    dataAnalyticsEvent?: string | null;
    dataAnalyticsProps?: string | null;
}) {
    const backgroundClassName =
        disabled === true
            ? tw`bg-ds-background-disabled`
            : isSelected === true
            ? tw`bg-ds-background-selected`
            : appearance === "discovery"
            ? tw`bg-ds-background-discovery-bold`
            : appearance === "primary"
            ? tw`bg-ds-background-brand-bold`
            : appearance === "subtle"
            ? tw`bg-ds-background-neutral-subtle`
            : tw`bg-ds-background-neutral-subtle`;
    const textClassName =
        disabled === true
            ? tw`text-ds-text-disabled`
            : isSelected === true
            ? tw`text-ds-text-selected`
            : appearance === "discovery"
            ? tw`text-ds-text-inverse`
            : appearance === "primary"
            ? tw`text-ds-text-inverse`
            : appearance === "subtle"
            ? tw`text-ds-text-subtle`
            : tw`text-ds-text-subtle`;
    const cursorClassName = isLoading === true ? tw`cursor-progress` : disabled === true ? tw`cursor-not-allowed` : null;
    const hoverBackgroundClassName =
        isLoading === true
            ? null
            : disabled === true
            ? null
            : isSelected === true
            ? tw`hover:bg-ds-background-selected-hovered`
            : appearance === "discovery"
            ? tw`hover:bg-ds-background-discovery-bold-hovered`
            : appearance === "primary"
            ? tw`hover:bg-ds-background-brand-bold-hovered`
            : appearance === "subtle"
            ? tw`hover:bg-ds-background-neutral-subtle-hovered`
            : tw`hover:bg-ds-background-neutral-subtle-hovered`;
    const activeBackgroundClassName =
        isLoading === true
            ? null
            : disabled === true
            ? null
            : isSelected === true
            ? tw`active:bg-ds-background-selected-pressed`
            : appearance === "discovery"
            ? tw`active:bg-ds-background-discovery-bold-pressed`
            : appearance === "primary"
            ? tw`active:bg-ds-background-brand-bold-pressed`
            : appearance === "subtle"
            ? tw`active:bg-ds-background-neutral-subtle-pressed`
            : tw`active:bg-ds-background-neutral-subtle-pressed`;
    // TODO: Figure out a better way of doing this?
    // Maybe using `::after` for consistency with ADS?
    const borderClassName =
        isSelected === true
            ? tw`outline-ds-border-width outline-solid outline-ds-border-selected -outline-offset-ds-border-width`
            : appearance === "discovery"
            ? null
            : appearance === "primary"
            ? null
            : appearance === "subtle"
            ? null
            : // TODO: Figure out a better way of doing this?
              // Maybe using `::after` for consistency with ADS?
              tw`outline-ds-border-width outline-solid outline-ds-border -outline-offset-ds-border-width`;
    const pClassName = spacing === "compact" ? tw`p-ds-space-025` : tw`p-ds-space-075`;
    const sizeClassName = spacing === "compact" ? tw`w-6 h-6` : showChevron === true ? tw`w-auto h-8` : tw`w-8 h-8`;
    const iconSizeClassName = spacing === "compact" ? tw`w-3 h-3` : tw`w-4 h-4`;
    const spinnerSizeClassName = spacing === "compact" ? tw`w-4 h-4` : tw`w-6 h-6`;

    const layoutClassName =
        content !== null && content !== undefined ? twJoin("grid grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-ds-space-075 gap-ds-space-025 items-center") : "w-full h-full";

    const Button = (
        <HeadlessUiButton
            type="button"
            onClick={onClick}
            data-testid={testId}
            {...toDataAnalyticsHtmlAttributes(dataAnalyticsEvent, dataAnalyticsProps)}
            className={twJoin(
                sizeClassName,
                pClassName,
                backgroundClassName,
                textClassName,
                cursorClassName,
                hoverBackgroundClassName,
                activeBackgroundClassName,
                borderClassName,
                "rounded-ds-border-radius-100",
                // "transition-colors duration-small",
                "relative",
                "group",
                className,
            )}
        >
            <div className={layoutClassName}>
                <div className={twJoin("w-full h-full", "grid place-items-center")}>
                    <div
                        className={twJoin(
                            "grid",
                            iconSizeClassName,
                            "transition-opacity duration-medium",
                            isLoading === true ? "opacity-0" : "opacity-100",
                            //
                        )}
                    >
                        <IconComponent className="w-full h-full" />
                    </div>
                </div>
                {Boolean(content) && <div className="max-w-[120px] whitespace-nowrap overflow-clip text-ds-font-heading-xsmall">{content}</div>}
                {showChevron === true && (
                    <div
                        className={twJoin(
                            "transition-opacity duration-small",
                            "sm:opacity-0 sm:group-hover:opacity-100",
                            // isSelected === true ? "opacity-100" : "opacity-0",
                        )}
                    >
                        <ChevronDownIcon />
                    </div>
                )}
            </div>

            <Transition show={isLoading === true}>
                <div
                    className={twJoin(
                        "absolute inset-0",
                        "grid place-items-center",
                        "transition-opacity duration-medium",
                        "opacity-100 data-closed:opacity-0",
                        //
                    )}
                    //
                >
                    <Spinner className={spinnerSizeClassName} />
                </div>
            </Transition>
        </HeadlessUiButton>
    );

    return tooltip !== undefined ? <Tooltip {...tooltip}>{Button}</Tooltip> : Button;
}
